import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { Bell, CheckCheck, CircleAlert, CircleCheckBig, Gauge, Globe, HeartPulse, PowerOff, RotateCcw, Settings as SettingsIcon, ShieldAlert, Trash2, X } from "lucide-react";

import { EmptyState, ago, useToast } from "./ui.jsx";
import { api } from "../lib/api.js";
import { useLive } from "../lib/events.js";

/**
 * What the phone threw away.
 *
 * A push notification is not a record of itself: swiping it out of a phone's
 * notification centre destroys it, a locked phone may never show it, and the
 * `tag` that stops a failing project becoming a column of identical alerts means
 * the second one deliberately replaces the first. The server writes down
 * everything it raises; this reads it back.
 */

const KIND = {
    "deploy.failed": { Icon: CircleAlert, tone: "text-red-500" },
    "deploy.rolled-back": { Icon: RotateCcw, tone: "text-amber-500" },
    "deploy.succeeded": { Icon: CircleCheckBig, tone: "text-green-500" },
    "service.down": { Icon: HeartPulse, tone: "text-red-500" },
    "site.down": { Icon: Globe, tone: "text-red-500" },
    // Retired in favour of service.down; still in the inbox for thirty days.
    "process.down": { Icon: PowerOff, tone: "text-red-500" },
    "resource.high": { Icon: Gauge, tone: "text-amber-500" },
    "cert.expiring": { Icon: ShieldAlert, tone: "text-amber-500" },
    test: { Icon: Bell, tone: "muted" }
};

/**
 * The number on the Home Screen icon.
 *
 * Supported where it is supported and ignored where it is not — nothing depends
 * on it, and it is refused outright in a browser tab on iOS.
 */
function setAppBadge(count) {
    try {
        if (!("setAppBadge" in navigator)) return;
        const done = count > 0 ? navigator.setAppBadge(count) : navigator.clearAppBadge();
        Promise.resolve(done).catch(() => {});
    } catch {
        // Not available. It is decoration.
    }
}

/**
 * The count and the list, kept current.
 *
 * The stream says the moment a notification is recorded, push or no push; the
 * service worker says when a push lands, which on a phone can be the earlier
 * of the two. The slow timer under `useLive` is the floor, not the mechanism.
 */
export function useInbox() {
    const [items, setItems] = useState([]);
    const [unread, setUnread] = useState(0);

    const refresh = useCallback(async () => {
        try {
            const data = await api.notifications.inbox();
            setItems(data.items ?? []);
            setUnread(data.unread ?? 0);
            setAppBadge(data.unread ?? 0);
        } catch {
            // The shell already says when the box is unreachable; a bell that
            // empties itself on one failed poll would be worse than a stale one.
        }
    }, []);

    useLive(["notifications"], refresh);

    useEffect(() => {
        const onMessage = (event) => {
            if (event.data?.type === "push") refresh();
        };
        navigator.serviceWorker?.addEventListener("message", onMessage);
        return () => navigator.serviceWorker?.removeEventListener("message", onMessage);
    }, [refresh]);

    /** Optimistic: the count is the point of the thing, so it must not lag a round trip. */
    const markRead = useCallback(async (ids) => {
        setItems((current) => current.map((item) => (!ids || ids.includes(item.id) ? { ...item, read: true } : item)));
        setUnread((current) => (ids ? Math.max(0, current - ids.length) : 0));
        setAppBadge(0);
        try {
            await api.notifications.markRead(ids);
        } finally {
            refresh();
        }
    }, [refresh]);

    const clearRead = useCallback(async () => {
        const removed = await api.notifications.clearRead();
        await refresh();
        return removed;
    }, [refresh]);

    return { items, unread, refresh, markRead, clearRead };
}

/** The bell itself, with its count. */
export function NotificationBell({ unread, onClick, className = "" }) {
    return (
        <button
            onClick={onClick}
            className={`muted relative hover:text-gray-900 dark:hover:text-gray-100 ${className}`}
            aria-label={unread > 0 ? `Notifications, ${unread} unread` : "Notifications"}
        >
            <Bell size={17} aria-hidden />
            {unread > 0 ? (
                <span className="absolute -right-1.5 -top-1.5 flex min-w-4 items-center justify-center rounded-full bg-blue-600 px-1 text-[10px] font-medium leading-4 text-white">
                    {unread > 9 ? "9+" : unread}
                </span>
            ) : null}
        </button>
    );
}

/**
 * A slide-over from the right rather than a dropdown.
 *
 * The navigation is on the left at every width, so there is nothing to anchor a
 * dropdown to on a phone, and a panel that is the same shape everywhere is one
 * behaviour to get right instead of two.
 */
export function NotificationPanel({ open, onClose, inbox }) {
    const navigate = useNavigate();
    const toast = useToast();
    const { items, unread, markRead, clearRead } = inbox;

    useEffect(() => {
        if (!open) return undefined;
        const onKey = (event) => event.key === "Escape" && onClose();
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [open, onClose]);

    if (!open) return null;

    const readCount = items.filter((item) => item.read).length;

    const openItem = (item) => {
        if (!item.read) markRead([item.id]);
        onClose();
        if (item.url) navigate(item.url);
    };

    return (
        <div className="fixed inset-0 z-50" role="dialog" aria-modal="true" aria-label="Notifications">
            <button className="absolute inset-0 bg-black/50" onClick={onClose} aria-label="Close notifications" tabIndex={-1} />

            <div className="absolute inset-y-0 right-0 flex w-full max-w-sm flex-col border-l border-gray-200 bg-white shadow-xl dark:border-gray-800 dark:bg-gray-950">
                <div className="flex items-center gap-2 border-b border-gray-200 px-4 py-3 pt-[max(0.75rem,env(safe-area-inset-top))] dark:border-gray-800">
                    <h2 className="section-title mr-auto">Notifications</h2>

                    {unread > 0 ? (
                        <button onClick={() => markRead()} className="muted hover:text-gray-900 dark:hover:text-gray-100" aria-label="Mark all as read" title="Mark all as read">
                            <CheckCheck size={16} />
                        </button>
                    ) : null}

                    {readCount > 0 ? (
                        <button
                            onClick={async () => {
                                const result = await clearRead();
                                toast(`Cleared ${result.removed} notification${result.removed === 1 ? "" : "s"}`);
                            }}
                            className="muted hover:text-gray-900 dark:hover:text-gray-100"
                            aria-label="Clear the ones already read"
                            title="Clear the ones already read"
                        >
                            <Trash2 size={15} />
                        </button>
                    ) : null}

                    <button onClick={onClose} className="muted hover:text-gray-900 dark:hover:text-gray-100" aria-label="Close notifications">
                        <X size={16} />
                    </button>
                </div>

                {items.length === 0 ? (
                    <EmptyState icon={Bell} title="Nothing yet" hint="Deploys, processes and certificates report here, whether or not the push reached a phone." />
                ) : (
                    <ul className="flex-1 divide-y divide-gray-100 overflow-y-auto dark:divide-gray-800">
                        {items.map((item) => {
                            const { Icon, tone } = KIND[item.event] ?? { Icon: Bell, tone: "muted" };
                            return (
                                <li key={item.id}>
                                    <button
                                        onClick={() => openItem(item)}
                                        data-unread={item.read ? undefined : "true"}
                                        className={`flex w-full gap-3 px-4 py-3 text-left transition-colors hover:bg-gray-50 dark:hover:bg-gray-900 ${
                                            item.read ? "" : "bg-blue-50/50 dark:bg-blue-950/20"
                                        }`}
                                    >
                                        <Icon size={16} className={`mt-0.5 shrink-0 ${tone}`} aria-hidden />
                                        <span className="min-w-0 flex-1">
                                            <span className="flex items-baseline gap-2">
                                                <span className={`min-w-0 flex-1 truncate text-sm ${item.read ? "text-gray-700 dark:text-gray-300" : "font-medium text-gray-900 dark:text-gray-100"}`}>
                                                    {item.title}
                                                </span>
                                                <span className="muted shrink-0 text-xs">{ago(item.at)}</span>
                                            </span>
                                            {item.body ? <span className="muted mt-0.5 line-clamp-2 block text-xs">{item.body}</span> : null}
                                        </span>
                                        {item.read ? null : <span className="mt-1.5 size-2 shrink-0 rounded-full bg-blue-500" aria-hidden />}
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                )}

                <div className="border-t border-gray-200 px-4 py-2.5 pb-[max(0.625rem,env(safe-area-inset-bottom))] dark:border-gray-800">
                    <button
                        onClick={() => {
                            onClose();
                            navigate("/settings");
                        }}
                        className="muted flex items-center gap-2 text-xs hover:text-gray-900 dark:hover:text-gray-100"
                    >
                        <SettingsIcon size={13} aria-hidden /> Notification settings
                    </button>
                </div>
            </div>
        </div>
    );
}
